import type { CatequistaDto, LoginResponseDto } from '@sheepcount/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { clearSession, getCatequista, getToken, saveSession } from './auth-storage';

interface AuthContextValue {
  token: string | null;
  catequista: CatequistaDto | null;
  isAdmin: boolean;
  login: (res: LoginResponseDto) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(getToken);
  const [catequista, setCatequista] = useState<CatequistaDto | null>(getCatequista);

  const login = useCallback((res: LoginResponseDto) => {
    saveSession(res.accessToken, res.catequista);
    setToken(res.accessToken);
    setCatequista(res.catequista);
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setCatequista(null);
  }, []);

  // Reidrata o catequista (inclui `role`) a partir da API — cobre sessões antigas
  // salvas no localStorage antes da introdução de perfis.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    void api<CatequistaDto>('/auth/me')
      .then((me) => {
        if (cancelled) return;
        setCatequista(me);
        saveSession(token, me);
      })
      .catch(() => {
        // 401 já redireciona via api(); outros erros mantêm a sessão local.
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({ token, catequista, isAdmin: catequista?.role === 'ADMIN', login, logout }),
    [token, catequista, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
